// Static PWA logo — a straight port of the desktop
// src/renderer/components/common/Logo.tsx SVG geometry with the
// animate-loading branch removed (the PWA never shows the logo as a
// loading spinner, so the conic-gradient foreignObject and its CSS
// drop out entirely). The gradient still reads --accent-primary from
// the host so it inherits the Govu teal across both apps.

interface LogoProps {
    className?: string
}

export function Logo({ className = '' }: LogoProps) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 206.53 117.09"
            className={className}
            aria-label="Govu"
        >
            <defs>
                <linearGradient
                    id="govu-pwa-logo-gradient"
                    gradientUnits="userSpaceOnUse"
                    x1="0"
                    y1="0"
                    x2="206.53"
                    y2="0"
                >
                    <stop offset="0%" stopColor="var(--accent-primary, #00d4aa)" />
                    <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
                <style>
                    {`
                        .govu-pwa-logo-fill { fill: url(#govu-pwa-logo-gradient); }
                        .govu-pwa-logo-rest { fill: #525252; }
                        .govu-pwa-logo-circle {
                            fill: none;
                            stroke: #525252;
                            stroke-miterlimit: 10;
                            stroke-width: 4px;
                        }
                    `}
                </style>
            </defs>

            <g id="Circle">
                <circle className="govu-pwa-logo-circle" cx="132.19" cy="58.55" r="56.55" />
            </g>

            <g id="G">
                <path className="govu-pwa-logo-fill" d="M24.82,57.69c-4.95,0-9.28-1.07-13-3.22-3.72-2.15-6.62-5.21-8.7-9.17-2.08-3.96-3.12-8.66-3.12-14.09s1.09-10.44,3.28-14.4c2.18-3.96,5.14-6.99,8.88-9.08,3.73-2.09,7.9-3.14,12.5-3.14,2.98,0,5.77.43,8.35,1.28,2.59.86,4.89,2.07,6.92,3.66,2.02,1.58,3.66,3.46,4.91,5.63,1.25,2.17,2.02,4.56,2.32,7.18h-12.45c-.25-1.09-.67-2.06-1.25-2.93-.58-.87-1.28-1.6-2.11-2.2-.83-.6-1.79-1.06-2.86-1.37-1.07-.31-2.26-.47-3.55-.47-2.54,0-4.74.62-6.6,1.85-1.86,1.24-3.29,3.03-4.3,5.37-1.01,2.35-1.51,5.18-1.51,8.51s.49,6.17,1.47,8.53,2.4,4.16,4.26,5.41c1.86,1.25,4.11,1.87,6.74,1.87,2.29,0,4.23-.38,5.84-1.14s2.83-1.85,3.67-3.26c.84-1.41,1.27-3.06,1.27-4.96l2.43.28h-12.76v-9.01h22.26v6.83c0,4.55-.98,8.48-2.95,11.77-1.96,3.29-4.66,5.83-8.1,7.61-3.43,1.78-7.38,2.67-11.84,2.67Z" />
            </g>
            <g id="O">
                <path className="govu-pwa-logo-fill" d="M80.69,57.73c-3.98,0-7.42-.83-10.33-2.48s-5.15-3.96-6.73-6.93c-1.57-2.97-2.36-6.43-2.36-10.38s.79-7.41,2.36-10.38c1.57-2.97,3.81-5.28,6.73-6.93,2.91-1.65,6.36-2.48,10.33-2.48s7.45.83,10.35,2.48c2.9,1.65,5.14,3.96,6.71,6.93,1.57,2.97,2.36,6.43,2.36,10.38s-.79,7.41-2.36,10.38c-1.57,2.97-3.81,5.28-6.71,6.93-2.9,1.65-6.35,2.48-10.35,2.48ZM80.69,48.4c1.53,0,2.82-.43,3.88-1.3,1.06-.87,1.87-2.09,2.43-3.66.55-1.57.83-3.42.83-5.55s-.28-4-.83-5.56c-.55-1.56-1.36-2.77-2.43-3.62-1.06-.85-2.36-1.28-3.88-1.28s-2.79.43-3.87,1.28c-1.08.86-1.89,2.06-2.44,3.62-.55,1.56-.83,3.42-.83,5.56s.28,3.98.83,5.55c.55,1.57,1.37,2.79,2.44,3.66,1.07.87,2.36,1.3,3.87,1.3Z" />
            </g>
            <g id="U">
                <path className="govu-pwa-logo-fill" d="M182.89,57.52c-2.68,0-5.02-.59-7-1.79-1.99-1.19-3.52-2.88-4.61-5.06-1.09-2.18-1.63-4.74-1.63-7.68v-24.3h12.06v21.91c0,2.17.55,3.87,1.65,5.1,1.1,1.23,2.63,1.84,4.59,1.84,1.29,0,2.43-.28,3.41-.85.98-.57,1.75-1.39,2.29-2.46.54-1.08.81-2.38.81-3.9v-21.63h12.06v38.27h-11.48l-.17-9.78h.8c-.99,3.12-2.52,5.62-4.58,7.51-2.06,1.88-4.8,2.83-8.22,2.83Z" />
            </g>

            <g>
                <g id="Play">
                    <path className="govu-pwa-logo-fill" d="M130.32,55.61l-19.84-31.16c-.94-1.48.12-3.41,1.87-3.41h39.68c1.75,0,2.81,1.93,1.87,3.41l-19.84,31.16c-.87,1.37-2.87,1.37-3.74,0Z" />
                </g>
                <g id="Top_line">
                    <rect className="govu-pwa-logo-rest" x="109.46" y="66.62" width="45.46" height="10.61" transform="translate(264.38 143.85) rotate(180)" />
                </g>
                <g id="Bottom_line">
                    <rect className="govu-pwa-logo-rest" x="109.46" y="87.88" width="45.46" height="10.61" transform="translate(264.38 186.37) rotate(180)" />
                </g>
            </g>
        </svg>
    )
}
